import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  alpha,
  IconButton,
  Button,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';

// Import your logo
import logo from '../../assets/WFHCafe_WObg.png';

interface LoginRequiredModalProps {
  open: boolean;
  onClose: () => void;
}

const LoginRequiredModal: React.FC<LoginRequiredModalProps> = ({ open, onClose }) => {
  const { mode } = useCustomTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const isDark = mode === 'dark';
  
  // Theme colors
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  
  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : '650px',
    maxHeight: isMobile ? '480px' : '400px',
    margin: 'auto',
    overflow: 'hidden',
    padding: 0,
    // Hide scrollbar but allow scrolling
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    },
  };

  const handleLogin = () => {
    navigate('/login');
    onClose();
  };

  const handleSignup = () => {
    navigate('/register');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      {/* Main Content Container - Scrollable content */}
      <Box sx={{
        p: isMobile ? 3 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Close Button */}
        <Box sx={{
          position: 'absolute',
          top: isMobile ? 16 : 20,
          right: isMobile ? 16 : 20,
          zIndex: 10,
        }}>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "medium" : "large"} />
          </IconButton>
        </Box>

        {/* Logo - Much Bigger */}
        <Box
          component="img"
          src={logo}
          alt="WFHCafe Logo"
          sx={{
            height: isMobile ? 120 : 140,
            width: 'auto',
            mb: isMobile ? 3 : 4,
            mx: 'auto',
            transition: 'all 0.3s ease',
            filter: isDark ? 'brightness(1.1)' : 'none',
            '&:hover': {
              transform: 'scale(1.05)',
            }
          }}
        />
        
        {/* Text Content */}
        <Box sx={{
          textAlign: 'center',
          mb: isMobile ? 4 : 5,
          flex: 1,
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '1.4rem' : '1.75rem',
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            Join the WFHCafe Community
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.85) : alpha(deepGreen, 0.8),
              fontSize: isMobile ? '0.95rem' : '1.1rem',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            To create and share your favorite work-from-home coffee recipes, 
            join discussions, and build communities with fellow remote workers
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              fontSize: isMobile ? '0.85rem' : '1rem',
              lineHeight: 1.5,
            }}
          >
            Sign in or create an account to start sharing your coffee creations, 
            saving recipes, and connecting with the community
          </Typography>
        </Box>

        {/* Buttons Container - No gap, using actual Button components */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0,
          mt: 'auto',
        }}>
          <Button
            variant="outlined"
            onClick={handleLogin}
            sx={{
              borderRadius: '20px 0 0 20px',
              textTransform: 'none',
              borderColor: goldColor,
              color: goldColor,
              fontWeight: 700,
              fontSize: isMobile ? '0.9rem' : '1rem',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              minWidth: '110px',
              borderWidth: '2px',
              borderRightWidth: 0,
              backgroundColor: isDark ? alpha('#000', 0.3) : alpha('#fff', 0.3),
              '&:hover': {
                borderColor: goldColor,
                borderWidth: '2px',
                borderRightWidth: 0,
                backgroundColor: isDark ? alpha(goldColor, 0.15) : alpha(goldColor, 0.1),
              }
            }}
          >
            Login
          </Button>
          
          <Button
            variant="contained"
            onClick={handleSignup}
            sx={{
              borderRadius: '0 20px 20px 0',
              textTransform: 'none',
              background: isDark ? 'rgba(255,255,255,0.15)' : lokiGradient,
              color: isDark ? '#fff' : goldColor,
              fontWeight: 700,
              fontSize: isMobile ? '0.9rem' : '1rem',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              minWidth: '110px',
              border: `2px solid ${alpha(goldColor, 0.3)}`,
              borderLeft: 'none',
              '&:hover': {
                background: isDark ? 'rgba(255,255,255,0.25)' : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
                transform: 'translateY(-1px)',
                borderLeft: 'none',
              }
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LoginRequiredModal;